// Shared template + branding config (used by public pages and the Settings branding editor)

export const TEMPLATES = [
  {
    id: "default", label: "Default",
    accent: "#dc2626", bg: "#0a0a0a", text: "#f0f0f0",
    headingFont: "'Inter', system-ui, sans-serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    bgImage: null,
    blurb: "Clean & modern. You pick the colours.",
  },
  {
    id: "carnival", label: "Carnival",
    accent: "#e85d04", bg: "#1a0a00", text: "#f5e6c8",
    headingFont: "'Cinzel', serif",
    bodyFont: "'Crimson Text', serif",
    bgImage: "/templates/carnival-bg.png",
    blurb: "Vintage dark fairground, amber & gold.",
  },
  {
    id: "haunt", label: "Haunt",
    accent: "#dc2626", bg: "#050505", text: "#e8e8e8",
    headingFont: "'Cinzel', serif",
    bodyFont: "'Inter', system-ui, sans-serif",
    bgImage: "/templates/haunt-bg.png",
    blurb: "Blood-moon house, red glow.",
  },
  {
    id: "asylum", label: "Asylum",
    accent: "#dc2626", bg: "#0c0c0c", text: "#d4d4d4",
    headingFont: "'Special Elite', monospace",
    bodyFont: "'Special Elite', monospace",
    bgImage: "/templates/asylum-bg.png",
    blurb: "Decayed institution, typewriter & dashes.",
  },
  {
    id: "crypt", label: "Crypt",
    accent: "#b91c1c", bg: "#080608", text: "#ccc0a8",
    headingFont: "'Cinzel', serif",
    bodyFont: "'Crimson Text', serif",
    bgImage: "/templates/crypt-bg.png",
    gold: "#c9a84c",
    blurb: "Candlelit cathedral, gold & deep red.",
  },
];

export const getTemplate = (id) => TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];

// ────────────────────────────────────────────────────────────────────────────
// Full preset branding definitions (scaretickets-template-presets blueprint).
// These populate every editor control when a preset is selected, and act as the
// baseline rendered on the public page (saved branding is merged ON TOP of these).
// ────────────────────────────────────────────────────────────────────────────
export const PRESETS = {
  default: {
    template_id: "default",
    accent_color: "#dc2626", bg_color: "#0a0a0a", text_color: "#f0f0f0",
    bg_image_url: null, bg_image_opacity: 0,
    title_font: "Inter", subtitle_font: "Inter", body_font: "Inter",
    logo_size: 80, logo_align: "left",
    header_bg_color: "#111111", header_bg_opacity: 100, header_bg_image_url: null,
    header_border_color: "#1f1f1f", header_border_width: 1,
    title_text: null, title_size: 32, title_weight: 700, title_color: "#ffffff",
    title_transform: "none", title_letter_spacing: 0, title_align: "left", title_padding_bottom: 6,
    subtitle_text: null, subtitle_size: 14, subtitle_weight: 400, subtitle_color: "#888888",
    subtitle_align: "left", subtitle_padding_bottom: 8,
    welcome_text: null, welcome_font: "Inter", welcome_size: 13, welcome_weight: 400,
    welcome_color: "#666666", welcome_align: "left", welcome_padding_bottom: 12,
    calendar_heading: "Pick your night", summary_heading: "Your Visit",
    timeslot_container_heading: "Choose a time", ticket_container_heading: "Select Tickets",
    cal_box_bg: "#111111", cal_box_bg_opacity: 100, cal_box_border_color: "#1f1f1f",
    cal_box_border_width: 1, cal_box_border_style: "solid", cal_box_border_radius: 5, cal_box_padding: 16,
    cell_bg: "#161616", cell_bg_opacity: 100, cell_font: "Inter", cell_font_size: 13,
    cell_font_color: "#555555", cell_border_color: "#1f1f1f", cell_border_width: 1,
    cell_border_style: "solid", cell_border_radius: 4,
    cell_open_bg: "#1a1a1a", cell_open_bg_opacity: 100, cell_open_font_color: "#f0f0f0",
    cell_open_border_color: "#2e2e2e", cell_selected_bg: "#dc2626", cell_selected_font_color: "#ffffff",
    panel_bg: "#111111", panel_bg_opacity: 100, panel_border_color: "#1f1f1f", panel_border_width: 1,
    panel_border_style: "solid", panel_border_radius: 5, panel_padding: 16,
    btn_bg: "#dc2626", btn_text_color: "#ffffff", btn_border_radius: 5, btn_font_size: 13,
    btn_font_weight: 600, btn_text_transform: "none", btn_letter_spacing: 0,
    slot_item_bg: "#1a1a1a", slot_item_bg_opacity: 100, slot_item_border_color: "#2e2e2e",
    slot_item_border_width: 1, slot_item_border_style: "solid", slot_item_border_radius: 5,
    slot_item_font_size: 13, slot_item_font_color: "#f0f0f0", slot_cta_text: "Continue →",
    ticket_item_bg: "#1a1a1a", ticket_item_bg_opacity: 100, ticket_item_border_color: "#2e2e2e",
    ticket_item_border_width: 1, ticket_item_border_style: "solid", ticket_item_border_radius: 5,
    ticket_name_color: "#f0f0f0", ticket_price_color: "#dc2626",
    fomo_enabled: true,
    fomo_indicators: [
      { id: "available", label: "Available", threshold: null, fixed: true, label_color: "#22c55e", cell_bg_color: "#22c55e", cell_bg_opacity: 15, cell_border_color: "#22c55e" },
      { id: "1", label: "Selling Fast", threshold: 50, label_color: "#eab308", cell_bg_color: "#eab308", cell_bg_opacity: 15, cell_border_color: "#eab308" },
      { id: "2", label: "Nearly Gone", threshold: 75, label_color: "#f97316", cell_bg_color: "#f97316", cell_bg_opacity: 15, cell_border_color: "#f97316" },
      { id: "3", label: "Sold Out", threshold: 100, label_color: "#dc2626", cell_bg_color: "#dc2626", cell_bg_opacity: 20, cell_border_color: "#dc2626" },
    ],
    slot_fomo_enabled: false, slot_fomo_indicators: [],
  },

  carnival: {
    template_id: "carnival",
    accent_color: "#e85d04", bg_color: "#1a0a00", text_color: "#f5e6c8",
    bg_image_url: "/templates/carnival-bg.png", bg_image_opacity: 72,
    title_font: "Cinzel", subtitle_font: "Crimson Text", body_font: "Crimson Text",
    logo_size: 80, logo_align: "center",
    header_bg_color: "#1a0a00", header_bg_opacity: 0, header_bg_image_url: null,
    header_border_color: "rgba(255,200,100,0.2)", header_border_width: 1,
    title_text: null, title_size: 36, title_weight: 700, title_color: "#ffd60a",
    title_transform: "uppercase", title_letter_spacing: 2, title_align: "center", title_padding_bottom: 8,
    subtitle_text: null, subtitle_size: 15, subtitle_weight: 400, subtitle_color: "#a08060",
    subtitle_align: "center", subtitle_padding_bottom: 8,
    welcome_text: null, welcome_font: "Crimson Text", welcome_size: 15, welcome_weight: 400,
    welcome_color: "#a08060", welcome_align: "center", welcome_padding_bottom: 12,
    calendar_heading: "Select Your Night", summary_heading: "Your Booking",
    timeslot_container_heading: "Choose Your Time", ticket_container_heading: "Select Tickets",
    cal_box_bg: "#1a0a00", cal_box_bg_opacity: 55, cal_box_border_color: "rgba(255,200,100,0.3)",
    cal_box_border_width: 1, cal_box_border_style: "solid", cal_box_border_radius: 6, cal_box_padding: 16,
    cell_bg: "#1a0a00", cell_bg_opacity: 0, cell_font: "Crimson Text", cell_font_size: 16,
    cell_font_color: "rgba(160,128,96,0.4)", cell_border_color: "rgba(255,200,100,0.1)", cell_border_width: 1,
    cell_border_style: "solid", cell_border_radius: 4,
    cell_open_bg: "#ffffff", cell_open_bg_opacity: 4, cell_open_font_color: "#f5e6c8",
    cell_open_border_color: "rgba(255,200,100,0.2)", cell_selected_bg: "#e85d04", cell_selected_font_color: "#ffffff",
    panel_bg: "#1a0a00", panel_bg_opacity: 65, panel_border_color: "rgba(255,200,100,0.3)", panel_border_width: 1,
    panel_border_style: "solid", panel_border_radius: 6, panel_padding: 16,
    btn_bg: "#e85d04", btn_text_color: "#ffffff", btn_border_radius: 5, btn_font_size: 12,
    btn_font_weight: 600, btn_text_transform: "uppercase", btn_letter_spacing: 2,
    slot_item_bg: "#1a0a00", slot_item_bg_opacity: 55, slot_item_border_color: "rgba(255,200,100,0.25)",
    slot_item_border_width: 1, slot_item_border_style: "solid", slot_item_border_radius: 4,
    slot_item_font_size: 14, slot_item_font_color: "#f5e6c8", slot_cta_text: "Choose Your Time",
    ticket_item_bg: "#1a0a00", ticket_item_bg_opacity: 55, ticket_item_border_color: "rgba(255,200,100,0.25)",
    ticket_item_border_width: 1, ticket_item_border_style: "solid", ticket_item_border_radius: 4,
    ticket_name_color: "#f5e6c8", ticket_price_color: "#ffd60a",
    fomo_enabled: true,
    fomo_indicators: [
      { id: "available", label: "Available", threshold: null, fixed: true, label_color: "#ffd60a", cell_bg_color: "#ffd60a", cell_bg_opacity: 10, cell_border_color: "rgba(255,214,10,0.4)" },
      { id: "1", label: "Selling Fast", threshold: 50, label_color: "#e85d04", cell_bg_color: "#e85d04", cell_bg_opacity: 15, cell_border_color: "#e85d04" },
      { id: "2", label: "Nearly Gone", threshold: 75, label_color: "#dc2626", cell_bg_color: "#dc2626", cell_bg_opacity: 20, cell_border_color: "#dc2626" },
      { id: "3", label: "Sold Out", threshold: 100, label_color: "#6a3a1a", cell_bg_color: "#3a1a0a", cell_bg_opacity: 80, cell_border_color: "#6a3a1a" },
    ],
    slot_fomo_enabled: false, slot_fomo_indicators: [],
  },

  haunt: {
    template_id: "haunt",
    accent_color: "#dc2626", bg_color: "#050505", text_color: "#e8e8e8",
    bg_image_url: "/templates/haunt-bg.png", bg_image_opacity: 65,
    title_font: "Cinzel", subtitle_font: "Inter", body_font: "Inter",
    logo_size: 80, logo_align: "left",
    header_bg_color: "#000000", header_bg_opacity: 40, header_bg_image_url: null,
    header_border_color: "rgba(220,38,38,0.2)", header_border_width: 1,
    title_text: null, title_size: 34, title_weight: 700, title_color: "#ffffff",
    title_transform: "none", title_letter_spacing: 1, title_align: "left", title_padding_bottom: 6,
    subtitle_text: null, subtitle_size: 13, subtitle_weight: 400, subtitle_color: "#555555",
    subtitle_align: "left", subtitle_padding_bottom: 8,
    welcome_text: null, welcome_font: "Inter", welcome_size: 13, welcome_weight: 400,
    welcome_color: "#555555", welcome_align: "left", welcome_padding_bottom: 12,
    calendar_heading: "Choose Your Date", summary_heading: "Your Visit",
    timeslot_container_heading: "Pick a Time", ticket_container_heading: "Select Tickets",
    cal_box_bg: "#050505", cal_box_bg_opacity: 65, cal_box_border_color: "rgba(220,38,38,0.35)",
    cal_box_border_width: 1, cal_box_border_style: "solid", cal_box_border_radius: 5, cal_box_padding: 16,
    cell_bg: "#050505", cell_bg_opacity: 0, cell_font: "Inter", cell_font_size: 13,
    cell_font_color: "#2a2a2a", cell_border_color: "#1a1a1a", cell_border_width: 1,
    cell_border_style: "solid", cell_border_radius: 4,
    cell_open_bg: "#dc2626", cell_open_bg_opacity: 5, cell_open_font_color: "#e8e8e8",
    cell_open_border_color: "rgba(220,38,38,0.2)", cell_selected_bg: "#dc2626", cell_selected_font_color: "#ffffff",
    panel_bg: "#050505", panel_bg_opacity: 65, panel_border_color: "rgba(220,38,38,0.35)", panel_border_width: 1,
    panel_border_style: "solid", panel_border_radius: 5, panel_padding: 16,
    btn_bg: "#dc2626", btn_text_color: "#ffffff", btn_border_radius: 5, btn_font_size: 13,
    btn_font_weight: 600, btn_text_transform: "none", btn_letter_spacing: 0,
    slot_item_bg: "#050505", slot_item_bg_opacity: 65, slot_item_border_color: "rgba(220,38,38,0.25)",
    slot_item_border_width: 1, slot_item_border_style: "solid", slot_item_border_radius: 4,
    slot_item_font_size: 13, slot_item_font_color: "#e8e8e8", slot_cta_text: "Select Time Slot →",
    ticket_item_bg: "#050505", ticket_item_bg_opacity: 65, ticket_item_border_color: "rgba(220,38,38,0.25)",
    ticket_item_border_width: 1, ticket_item_border_style: "solid", ticket_item_border_radius: 4,
    ticket_name_color: "#e8e8e8", ticket_price_color: "#dc2626",
    fomo_enabled: true,
    fomo_indicators: [
      { id: "available", label: "Available", threshold: null, fixed: true, label_color: "#22c55e", cell_bg_color: "#22c55e", cell_bg_opacity: 10, cell_border_color: "rgba(34,197,94,0.4)" },
      { id: "1", label: "Selling Fast", threshold: 50, label_color: "#f97316", cell_bg_color: "#f97316", cell_bg_opacity: 12, cell_border_color: "rgba(249,115,22,0.5)" },
      { id: "2", label: "Nearly Gone", threshold: 75, label_color: "#dc2626", cell_bg_color: "#dc2626", cell_bg_opacity: 15, cell_border_color: "rgba(220,38,38,0.6)" },
      { id: "3", label: "Sold Out", threshold: 100, label_color: "#555555", cell_bg_color: "#111111", cell_bg_opacity: 80, cell_border_color: "#333333" },
    ],
    slot_fomo_enabled: false, slot_fomo_indicators: [],
  },

  asylum: {
    template_id: "asylum",
    accent_color: "#dc2626", bg_color: "#0c0c0c", text_color: "#d4d4d4",
    bg_image_url: "/templates/asylum-bg.png", bg_image_opacity: 75,
    title_font: "Special Elite", subtitle_font: "Special Elite", body_font: "Special Elite",
    logo_size: 70, logo_align: "left",
    header_bg_color: "#0c0c0c", header_bg_opacity: 0, header_bg_image_url: null,
    header_border_color: "#1e1e1e", header_border_width: 1,
    title_text: null, title_size: 28, title_weight: 400, title_color: "#d4d4d4",
    title_transform: "none", title_letter_spacing: 0, title_align: "left", title_padding_bottom: 4,
    subtitle_text: null, subtitle_size: 11, subtitle_weight: 400, subtitle_color: "#6a6a6a",
    subtitle_align: "left", subtitle_padding_bottom: 8,
    welcome_text: null, welcome_font: "Special Elite", welcome_size: 12, welcome_weight: 400,
    welcome_color: "#6a6a6a", welcome_align: "left", welcome_padding_bottom: 12,
    calendar_heading: "Pick Your Night", summary_heading: "Admission Record",
    timeslot_container_heading: "Select Entry Time", ticket_container_heading: "Select Tickets",
    cal_box_bg: "#0c0c0c", cal_box_bg_opacity: 80, cal_box_border_color: "#333333",
    cal_box_border_width: 1, cal_box_border_style: "dashed", cal_box_border_radius: 2, cal_box_padding: 16,
    cell_bg: "#0c0c0c", cell_bg_opacity: 0, cell_font: "Special Elite", cell_font_size: 13,
    cell_font_color: "#4a4a4a", cell_border_color: "#1e1e1e", cell_border_width: 1,
    cell_border_style: "solid", cell_border_radius: 2,
    cell_open_bg: "#0c0c0c", cell_open_bg_opacity: 0, cell_open_font_color: "#d4d4d4",
    cell_open_border_color: "#1e1e1e", cell_selected_bg: "#dc2626", cell_selected_font_color: "#ffffff",
    panel_bg: "#0c0c0c", panel_bg_opacity: 80, panel_border_color: "#333333", panel_border_width: 1,
    panel_border_style: "dashed", panel_border_radius: 2, panel_padding: 16,
    btn_bg: "#dc2626", btn_text_color: "#ffffff", btn_border_radius: 2, btn_font_size: 12,
    btn_font_weight: 400, btn_text_transform: "uppercase", btn_letter_spacing: 2,
    slot_item_bg: "#0c0c0c", slot_item_bg_opacity: 70, slot_item_border_color: "#1e1e1e",
    slot_item_border_width: 1, slot_item_border_style: "dashed", slot_item_border_radius: 2,
    slot_item_font_size: 13, slot_item_font_color: "#d4d4d4", slot_cta_text: "Proceed to Entry Selection",
    ticket_item_bg: "#0c0c0c", ticket_item_bg_opacity: 70, ticket_item_border_color: "#1e1e1e",
    ticket_item_border_width: 1, ticket_item_border_style: "dashed", ticket_item_border_radius: 2,
    ticket_name_color: "#d4d4d4", ticket_price_color: "#dc2626",
    fomo_enabled: true,
    fomo_indicators: [
      { id: "available", label: "Cleared", threshold: null, fixed: true, label_color: "#6a6a6a", cell_bg_color: "#1e1e1e", cell_bg_opacity: 60, cell_border_color: "#333333" },
      { id: "1", label: "Filling Up", threshold: 50, label_color: "#eab308", cell_bg_color: "#eab308", cell_bg_opacity: 10, cell_border_color: "#eab308" },
      { id: "2", label: "Restricted", threshold: 75, label_color: "#dc2626", cell_bg_color: "#dc2626", cell_bg_opacity: 12, cell_border_color: "#dc2626" },
      { id: "3", label: "Capacity Reached", threshold: 100, label_color: "#4a4a4a", cell_bg_color: "#111111", cell_bg_opacity: 90, cell_border_color: "#2a2a2a" },
    ],
    slot_fomo_enabled: false, slot_fomo_indicators: [],
  },

  crypt: {
    template_id: "crypt",
    accent_color: "#b91c1c", bg_color: "#080608", text_color: "#ccc0a8",
    bg_image_url: "/templates/crypt-bg.png", bg_image_opacity: 65,
    title_font: "Cinzel", subtitle_font: "Crimson Text", body_font: "Crimson Text",
    logo_size: 80, logo_align: "center",
    header_bg_color: "#080608", header_bg_opacity: 0, header_bg_image_url: null,
    header_border_color: "rgba(201,168,76,0.15)", header_border_width: 1,
    title_text: null, title_size: 34, title_weight: 700, title_color: "#ccc0a8",
    title_transform: "none", title_letter_spacing: 1, title_align: "center", title_padding_bottom: 6,
    subtitle_text: null, subtitle_size: 13, subtitle_weight: 400, subtitle_color: "#5a5040",
    subtitle_align: "center", subtitle_padding_bottom: 8,
    welcome_text: null, welcome_font: "Crimson Text", welcome_size: 15, welcome_weight: 400,
    welcome_color: "#5a5040", welcome_align: "center", welcome_padding_bottom: 12,
    calendar_heading: "Select Your Date", summary_heading: "Your Visit",
    timeslot_container_heading: "Choose a Time", ticket_container_heading: "Select Tickets",
    cal_box_bg: "#080608", cal_box_bg_opacity: 70, cal_box_border_color: "rgba(201,168,76,0.25)",
    cal_box_border_width: 1, cal_box_border_style: "solid", cal_box_border_radius: 5, cal_box_padding: 16,
    cell_bg: "#080608", cell_bg_opacity: 0, cell_font: "Crimson Text", cell_font_size: 16,
    cell_font_color: "#5a5040", cell_border_color: "rgba(201,168,76,0.1)", cell_border_width: 1,
    cell_border_style: "solid", cell_border_radius: 4,
    cell_open_bg: "#ffffff", cell_open_bg_opacity: 3, cell_open_font_color: "#ccc0a8",
    cell_open_border_color: "rgba(201,168,76,0.15)", cell_selected_bg: "#b91c1c", cell_selected_font_color: "#ffffff",
    panel_bg: "#080608", panel_bg_opacity: 70, panel_border_color: "rgba(201,168,76,0.25)", panel_border_width: 1,
    panel_border_style: "solid", panel_border_radius: 5, panel_padding: 16,
    btn_bg: "#b91c1c", btn_text_color: "#ffffff", btn_border_radius: 4, btn_font_size: 12,
    btn_font_weight: 600, btn_text_transform: "uppercase", btn_letter_spacing: 2,
    slot_item_bg: "#080608", slot_item_bg_opacity: 70, slot_item_border_color: "rgba(201,168,76,0.2)",
    slot_item_border_width: 1, slot_item_border_style: "solid", slot_item_border_radius: 4,
    slot_item_font_size: 14, slot_item_font_color: "#ccc0a8", slot_cta_text: "Select a Time",
    ticket_item_bg: "#080608", ticket_item_bg_opacity: 70, ticket_item_border_color: "rgba(201,168,76,0.2)",
    ticket_item_border_width: 1, ticket_item_border_style: "solid", ticket_item_border_radius: 4,
    ticket_name_color: "#ccc0a8", ticket_price_color: "#c9a84c",
    fomo_enabled: true,
    fomo_indicators: [
      { id: "available", label: "Available", threshold: null, fixed: true, label_color: "#c9a84c", cell_bg_color: "#c9a84c", cell_bg_opacity: 8, cell_border_color: "rgba(201,168,76,0.3)" },
      { id: "1", label: "Filling", threshold: 50, label_color: "#c9a84c", cell_bg_color: "#c9a84c", cell_bg_opacity: 18, cell_border_color: "rgba(201,168,76,0.6)" },
      { id: "2", label: "Nearly Full", threshold: 75, label_color: "#b91c1c", cell_bg_color: "#b91c1c", cell_bg_opacity: 15, cell_border_color: "rgba(185,28,28,0.6)" },
      { id: "3", label: "Sealed", threshold: 100, label_color: "#3a2a1a", cell_bg_color: "#1a1008", cell_bg_opacity: 90, cell_border_color: "#3a2a1a" },
    ],
    slot_fomo_enabled: false, slot_fomo_indicators: [],
  },
};

// Full preset branding for a template id (falls back to default).
export const presetBranding = (id) => PRESETS[id] || PRESETS.default;

// Effective branding rendered on the public page / preview: preset defaults with the
// attraction's saved (sparse) branding layered ON TOP. Saved values always win.
export function mergeBranding(branding) {
  const b = branding || {};
  const id = b.template_id || "default";
  return { ...presetBranding(id), ...b };
}

// Strip a full branding object down to only the fields that differ from its preset
// default (keeps `template_id`). Persisting this keeps the DB clean and lets future
// preset tweaks propagate to fields the operator never customised.
export function sparseBranding(branding) {
  const b = branding || {};
  const id = b.template_id || "default";
  const preset = presetBranding(id);
  const out = { template_id: id };
  for (const [k, v] of Object.entries(b)) {
    if (k === "template_id") continue;
    if (JSON.stringify(v) !== JSON.stringify(preset[k])) out[k] = v;
  }
  return out;
}

// Resolve effective branding for callers that need a flat summary (colours, fonts,
// bg image, logo, headings). Preset defaults are merged under the saved branding.
export function resolveBranding(attraction) {
  const b = mergeBranding((attraction && attraction.branding) || {});
  const t = getTemplate(b.template_id || "default");
  const fontStack = (name, fallback) => (name ? `'${name}', ${fallback}` : null);
  return {
    templateId: t.id,
    accent: b.accent_color || t.accent,
    bg: b.bg_color || t.bg,
    text: b.text_color || t.text,
    headingFont: fontStack(b.title_font || b.heading_font, "serif") || t.headingFont,
    bodyFont: fontStack(b.body_font, "sans-serif") || t.bodyFont,
    headingFontName: b.title_font || b.heading_font || null,
    bodyFontName: b.body_font || null,
    bgImage: b.bg_image_url || t.bgImage,
    bgOpacity: b.bg_image_opacity != null ? b.bg_image_opacity / 100 : 0.8,
    logo: b.logo_url || null,
    hero: b.hero_url || null,
    titleText: b.title_text || null,
    subtitleText: b.subtitle_text || null,
    bgMode: b.bg_mode || null,
  };
}
